import { useEdgesState, useNodesState } from 'reactflow';
import { useTransactionById } from '../useTransactionById';
import { useComposedNodesFromTransaction } from './helper';

/**
 * Custom hook to manage nodes and edges from transactions.
 * This hook fetches transaction data, parses it into nodes and edges, and manages their state.
 *
 * @returns {Object} - An object containing the transaction query, nodes, edges, and state change handlers.
 *
 * Example usage:
 * const { transactionByIdQuery, nodes, edges, onNodesChange, onEdgesChange } = useNodesFromTransactions();
 */
export function useNodesFromTransactions() {
  // Fetch transaction data by ID using a custom hook
  const transactionByIdQuery = useTransactionById();
  const { transaction } = transactionByIdQuery;

  // Parse the transaction data into nodes and edges using a custom hook
  const { edges: parsedEdges, nodes: parsedNodes } =
    useComposedNodesFromTransaction(transaction?.transaction);

  // Manage the state of nodes using the useNodesState hook from reactflow
  const [nodes, setNodes, onNodesChange] = useNodesState(parsedNodes);

  // Manage the state of edges using the useEdgesState hook from reactflow
  const [edges, setEdges, onEdgesChange] = useEdgesState(parsedEdges);

  // Return the transaction query, nodes, edges, and state change handlers
  return {
    transactionByIdQuery,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
  };
}
